<template>
  <v-container>
    <!-- Título e Botões -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-file-document-edit-outline</v-icon>
          Prescrições
        </h3>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-3"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Botão para atualizar a lista -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              class="mr-3"
              @click="loadPrescriptions"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>

        <!-- Botão para gerenciar timbre -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="indigo"
              v-bind="attrs"
              v-on="on"
              @click="openLetterheadDialog"
              class="mr-3"
              data-tour="pr-timbre"
            >
              <v-icon>mdi-file-image</v-icon>
            </v-btn>
          </template>
          <span>Gerenciar timbre da prescrição</span>
        </v-tooltip>

        <!-- Botão para adicionar prescrição -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="openPrescriptionDialog"
              class="mr-3"
              color="primary"
              data-tour="pr-nova"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Nova prescrição</span>
        </v-tooltip>

        <!-- Botão para buscar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              :color="showSearch ? 'secondary' : 'primary'"
              v-bind="attrs"
              v-on="on"
              @click="toggleSearch"
              class="mr-3"
              data-tour="pr-busca"
            >
              <v-icon>{{ showSearch ? "mdi-magnify-close" : "mdi-magnify" }}</v-icon>
            </v-btn>
          </template>
          <span>{{ showSearch ? "Fechar busca" : "Buscar prescrições" }}</span>
        </v-tooltip>

        <!-- Botão de ordenação -->
        <v-menu v-model="menuOpen" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="{ ...menuAttrs, ...tipAttrs }"
                  v-on="{ ...menuOn, ...tipOn }"
                >
                  <v-icon>mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Ordenar por data</span>
            </v-tooltip>
          </template>

          <v-list>
            <v-list-item @click="sortprescriptions('asc')">
              <v-list-item-title>Crescente</v-list-item-title>
            </v-list-item>
            <v-list-item @click="sortprescriptions('desc')">
              <v-list-item-title>Decrescente</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Campo de busca (abre/fecha pelo botão da lupa) -->
    <v-expand-transition>
      <v-row v-show="showSearch" class="mb-2" no-gutters>
        <v-col cols="12">
          <v-text-field
            ref="searchField"
            v-model="search"
            outlined
            dense
            clearable
            hide-details
            placeholder="Buscar por data, tipo, status, medicamento, exame, TUSS ou CID..."
            prepend-inner-icon="mdi-magnify"
            @keydown.esc="closeSearch"
          >
            <template v-slot:append>
              <span class="search-count">
                {{ filteredPrescriptions.length }} de {{ prescriptions.length }}
              </span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Tabela -->
    <v-data-table
      :headers="headers"
      :items="filteredPrescriptions"
      :loading="loading"
      item-key="id"
      class="elevation-1"
      data-tour="pr-tabela"
      :no-data-text="noDataText"
      loading-text="Carregando prescrições..."
      :footer-props="{
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
      }"
    >
      <!-- Slot para ações -->
      <template v-slot:[`item.actions`]="{ item, index }">
        <div class="actions-cell-inner">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="info"
                v-bind="attrs"
                v-on="on"
                @click="viewPrescriptionDetails(item)"
                class="mr-1"
                :data-tour="index === 0 ? 'pr-detalhes' : null"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Ver detalhes da prescrição</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="indigo"
                v-bind="attrs"
                v-on="on"
                :loading="downloadingId === item.id"
                :disabled="downloadingId !== null"
                @click="downloadPrescription(item)"
                class="mr-1"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Baixar PDF para impressão</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="confirmDelete(item)"
                color="error"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Excluir prescrição</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Slot para tipo traduzido -->
      <template v-slot:[`item.prescription_type`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              :color="getTypeColor(item.prescription_type)"
              dark
              v-bind="attrs"
              v-on="on"
            >
              {{ translateType(item.prescription_type) }}
            </v-chip>
          </template>
          <span>{{ getTypeTooltip(item.prescription_type) }}</span>
        </v-tooltip>
      </template>

      <!-- Slot para status com chip colorido -->
      <template v-slot:[`item.status`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              :color="getStatusColor(item.status)"
              dark
              v-bind="attrs"
              v-on="on"
            >
              {{ translateStatus(item.status) }}
            </v-chip>
          </template>
          <span>{{ getStatusTooltip(item.status) }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- Modal de Prescrição -->
    <PrescriptionDialog
      v-model="showPrescriptionDialog"
      @saved="handlePrescriptionSaved"
    />

    <!-- Modal de Upload de Timbre -->
    <LetterheadUploadDialog v-model="showLetterheadDialog" />

    <!-- Modal de Detalhes da Prescrição -->
    <v-dialog v-model="showDetailsDialog" width="1500px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header py-4">
          <span class="ds-dialog-title">Detalhes da Prescrição #{{ selectedPrescription?.id }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="close-btn"
                color="white"
                v-bind="attrs"
                v-on="on"
                @click="showDetailsDialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Informações da Prescrição -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span>Informações da Prescrição</span>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <strong>Data:</strong> {{ selectedPrescription?.created_at }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <strong>Tipo:</strong>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        :color="
                          getTypeColor(selectedPrescription?.prescription_type)
                        "
                        dark
                        class="ml-2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{
                          translateType(selectedPrescription?.prescription_type)
                        }}
                      </v-chip>
                    </template>
                    <span>{{
                      getTypeTooltip(selectedPrescription?.prescription_type)
                    }}</span>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <strong>Total de Itens:</strong>
                  {{ (selectedPrescription?.items || []).length }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <strong>Status:</strong>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        :color="getStatusColor(selectedPrescription?.status)"
                        dark
                        class="ml-2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ translateStatus(selectedPrescription?.status) }}
                      </v-chip>
                    </template>
                    <span>{{
                      getStatusTooltip(selectedPrescription?.status)
                    }}</span>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col cols="12" md="6" v-if="selectedPrescription?.doctor?.name">
                <div class="info-item">
                  <strong>Prescrito por:</strong>
                  {{ selectedPrescription.doctor.name }}
                </div>
              </v-col>
            </v-row>
          </section>

          <!-- CIDs vinculados à prescrição -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-clipboard-pulse-outline</v-icon>
                <span>CIDs da prescrição</span>
              </div>
            </div>
            <div v-if="(selectedPrescription?.conditions || []).length">
              <v-tooltip
                bottom
                v-for="cid in selectedPrescription.conditions"
                :key="cid.id"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    outlined
                    color="#0E7490"
                    class="mr-2 mb-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <strong class="mr-1">{{ cid.code }}</strong>
                    <span class="cid-desc">{{ cid.description }}</span>
                  </v-chip>
                </template>
                <span>{{ cid.code }} — {{ cid.description }}</span>
              </v-tooltip>
            </div>
            <div v-else class="text-caption grey--text">
              Nenhum CID vinculado a esta prescrição.
            </div>
          </section>

          <!-- Tabela de Itens -->
          <section class="ds-form-section ds-form-section--flush">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">{{ getTypeIcon(selectedType) }}</v-icon>
                <span>{{ getItemsTitle(selectedType) }}</span>
              </div>
              <span class="ds-form-section__aside">{{ (selectedPrescription?.items || []).length }}</span>
            </div>
            <v-data-table
              :headers="currentItemsHeaders"
              :items="selectedPrescription?.items || []"
              item-key="id"
              :no-data-text="getNoItemsText(selectedType)"
              :footer-props="{
                itemsPerPageText: 'Itens por página:',
              }"
            >
              <!-- Slot para dosagem + frequência (só se existir) -->
              <template v-slot:[`item.dosage_info`]="{ item }">
                <span v-if="item.dosage || item.frequency">
                  {{ item.dosage || "-" }} / {{ item.frequency || "-" }}
                </span>
                <span v-else>-</span>
              </template>

              <!-- Slot para tipo do item (usado quando a prescrição é mista) -->
              <template v-slot:[`item.item_type`]="{ item }">
                <v-chip small :color="getTypeColor(item.item_type)" dark>
                  <v-icon left small>{{ getTypeIcon(item.item_type) }}</v-icon>
                  {{ translateType(item.item_type) }}
                </v-chip>
              </template>

              <!-- Campos genéricos que podem vir vazios -->
              <template v-slot:[`item.tuss_code`]="{ item }">
                {{ item.tuss_code || "-" }}
              </template>
              <template v-slot:[`item.presentation`]="{ item }">
                {{ item.presentation || "-" }}
              </template>
              <template v-slot:[`item.duration`]="{ item }">
                {{ item.duration || "-" }}
              </template>
              <template v-slot:[`item.administration_route`]="{ item }">
                {{ item.administration_route || "-" }}
              </template>
              <template v-slot:[`item.quantity`]="{ item }">
                {{ item.quantity || "-" }}
              </template>
              <template v-slot:[`item.instructions`]="{ item }">
                <v-tooltip bottom v-if="item.instructions">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="text-truncate"
                      style="max-width: 150px; display: inline-block"
                    >
                      {{ item.instructions }}
                    </span>
                  </template>
                  <span>{{ item.instructions }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>

              <!-- CIDs vinculados a cada item -->
              <template v-slot:[`item.cids`]="{ item }">
                <template v-if="(item.conditions || []).length">
                  <v-tooltip
                    bottom
                    v-for="cid in item.conditions"
                    :key="cid.id"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        outlined
                        color="#0E7490"
                        class="mr-1 mb-1"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ cid.code }}
                      </v-chip>
                    </template>
                    <span>{{ cid.code }} — {{ cid.description }}</span>
                  </v-tooltip>
                </template>
                <span v-else>-</span>
              </template>

              <!-- Slot para observações com tooltip -->
              <template v-slot:[`item.notes`]="{ item }">
                <v-tooltip bottom v-if="item.notes">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="text-truncate"
                      style="max-width: 150px; display: inline-block"
                    >
                      {{ item.notes }}
                    </span>
                  </template>
                  <span>{{ item.notes }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </section>

          <!-- Conteúdo completo (texto integral da prescrição, se houver) -->
          <section v-if="plainFullContent" class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                <span>Conteúdo completo</span>
              </div>
            </div>
            <pre class="full-content-box">{{ plainFullContent }}</pre>
          </section>
        </v-card-text>

        <!-- Rodapé do Modal -->
        <v-card-actions class="ds-dialog-actions">
          <v-btn
            outlined
            color="primary"
            @click="showDetailsDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            Fechar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="downloadingId === selectedPrescription?.id"
            :disabled="downloadingId !== null"
            @click="downloadPrescription(selectedPrescription)"
          >
            <v-icon left>mdi-download</v-icon>
            Baixar PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import http from "@/services/http";
import PrescriptionDialog from "@/features/patients/prescriptions/PrescriptionDialog.vue";
import LetterheadUploadDialog from "@/features/patients/prescriptions/LetterheadUploadDialog.vue";
import { useSnackbar } from "@/composables/useSnackbar";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate } from "@/utils/datetime";
import {
  downloadPrescriptionPdf,
  buildPrescriptionFileName,
} from "@/features/patients/prescriptions/utils/prescriptionPdf";
import {
  resolvePreferredLetterhead,
  fetchLetterheadImageUrl,
} from "@/services/letterheadService";
import { getPrescriptionDocument } from "@/services/prescriptionService";
export default {
  name: "Prescriptions",
  mixins: [tourMixin],
  components: {
    PrescriptionDialog,
    LetterheadUploadDialog,
  },
  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },
  data() {
    return {
      menuOpen: false,
      loading: false,
      showPrescriptionDialog: false,
      showLetterheadDialog: false,
      showDetailsDialog: false,
      showSearch: false,
      search: "",
      // id da prescrição cujo PDF está sendo gerado (null = nenhum)
      downloadingId: null,
      isDeleting: false,
      selectedPrescription: null,
      prescriptionToDelete: null,
      headers: [
        { text: "Data",   value: "created_at", sortable: true },
        { text: "Tipo",   value: "prescription_type" },
        { text: "Itens",  value: "items_count", align: "center" },
        { text: "Status", value: "status" },
        { text: "Ações",  value: "actions", sortable: false, align: "center" },
      ],
      itemsHeadersByType: {
        medication: [
          { text: "Código TUSS", value: "tuss_code" },
          { text: "Medicação", value: "medication_name" },
          { text: "Apresentação", value: "presentation" },
          { text: "Quantidade", value: "quantity" },
          { text: "Dosagem / Frequência", value: "dosage_info" },
          { text: "Duração", value: "duration" },
          { text: "Via", value: "administration_route" },
          { text: "Orientações", value: "instructions" },
          { text: "CIDs", value: "cids", sortable: false },
          { text: "Observações", value: "notes" },
        ],
        exam: [
          { text: "Código TUSS", value: "tuss_code" },
          { text: "Exame", value: "medication_name" },
          { text: "Quantidade", value: "quantity" },
          { text: "Orientações", value: "instructions" },
          { text: "CIDs", value: "cids", sortable: false },
          { text: "Observações", value: "notes" },
        ],
        supplement: [
          { text: "Suplemento", value: "medication_name" },
          { text: "Apresentação", value: "presentation" },
          { text: "Quantidade", value: "quantity" },
          { text: "Dosagem / Frequência", value: "dosage_info" },
          { text: "Duração", value: "duration" },
          { text: "Orientações", value: "instructions" },
          { text: "CIDs", value: "cids", sortable: false },
          { text: "Observações", value: "notes" },
        ],
        // Para prescrições mistas, mostra coluna de tipo + campos comuns
        mixed: [
          { text: "Tipo", value: "item_type" },
          { text: "Nome", value: "medication_name" },
          { text: "Apresentação", value: "presentation" },
          { text: "Quantidade", value: "quantity" },
          { text: "Dosagem / Frequência", value: "dosage_info" },
          { text: "Duração", value: "duration" },
          { text: "Orientações", value: "instructions" },
          { text: "CIDs", value: "cids", sortable: false },
          { text: "Observações", value: "notes" },
        ],
      },
      prescriptions: [],
    };
  },
  mounted() {
    this.loadPrescriptions();
  },
  watch: {
    // Ao fechar o gerenciador de timbre o arquivo pode ter mudado; descarta o
    // cache pra que o próximo PDF use a versão nova.
    showLetterheadDialog(open, wasOpen) {
      if (wasOpen && !open) this._letterheadPromise = null;
    },
  },
  computed: {
    tourId() {
      return "sec-prescricoes-pg";
    },
    // Tipo da prescrição sendo exibida no modal (normalizado)
    selectedType() {
      const raw = this.selectedPrescription?.prescription_type;
      if (!raw) return "medication";
      const key = raw.toLowerCase();
      return ["medication", "exam", "supplement", "mixed"].includes(key)
        ? key
        : "medication";
    },
    // A busca é local: a lista inteira do paciente já veio do backend, então
    // filtrar em memória evita ida e volta e responde a cada tecla.
    filteredPrescriptions() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return this.prescriptions;

      // Cada termo separado por espaço precisa aparecer em algum campo.
      const terms = term.split(/\s+/);
      return this.prescriptions.filter((p) => {
        const haystack = this.buildSearchHaystack(p);
        return terms.every((t) => haystack.includes(t));
      });
    },
    noDataText() {
      if (this.search && this.prescriptions.length) {
        return "Nenhuma prescrição corresponde à busca";
      }
      return "Nenhuma prescrição encontrada";
    },
    // Headers da tabela do modal dinâmicos de acordo com o tipo
    currentItemsHeaders() {
      return (
        this.itemsHeadersByType[this.selectedType] ||
        this.itemsHeadersByType.medication
      );
    },

    // full_content é HTML do editor (com botões "×", estilos, etc). Aqui
    // convertemos para TEXTO limpo: removemos botões e tags, viramos blocos em
    // quebras de linha e decodificamos entidades. Nada é executado como HTML.
    plainFullContent() {
      const html = this.selectedPrescription?.full_content || "";
      if (!html) return "";
      let s = html
        .replace(/<button[\s\S]*?<\/button>/gi, "") // remove botões (× etc.)
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(div|p|li|tr|h[1-6])>/gi, "\n") // blocos → nova linha
        .replace(/<[^>]+>/g, ""); // remove as demais tags
      // Decodifica entidades (&amp;, &nbsp; ...) sem executar scripts.
      const ta = document.createElement("textarea");
      ta.innerHTML = s;
      s = ta.value;
      return s
        .replace(/ /g, " ") // nbsp → espaço
        .replace(/[ \t]+\n/g, "\n") // limpa espaços à direita
        .replace(/\n{3,}/g, "\n\n") // no máx. uma linha em branco
        .trim();
    },
  },
  methods: {
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      return [
        {
          title: "Prescrições do paciente",
          description:
            "Aqui você gerencia todas as prescrições deste paciente. Vamos conhecer os principais recursos.",
        },
        {
          anchor: "pr-nova",
          title: "Nova prescrição",
          description:
            "Clique aqui para criar uma nova prescrição de medicamentos, exames ou suplementos.",
        },
        {
          anchor: "pr-timbre",
          title: "Papel timbrado",
          description:
            "Envie e gerencie o timbre que será usado no cabeçalho das prescrições impressas.",
        },
        {
          anchor: "pr-busca",
          title: "Buscar prescrições",
          description:
            "Filtre a lista por data, tipo, status ou pelo conteúdo — nome do medicamento, exame, código TUSS ou CID.",
        },
        {
          anchor: "pr-tabela",
          title: "Lista de prescrições",
          description:
            "Todas as prescrições aparecem nesta tabela, com data, tipo e status de cada uma.",
        },
        {
          anchor: "pr-detalhes",
          title: "Ver detalhes",
          description:
            "Use este botão para abrir os detalhes completos da prescrição, incluindo itens e CIDs.",
        },
      ];
    },
    async loadPrescriptions() {
      this.loading = true;
      try {
        const patientId = this.$route.params.id;
        if (!patientId) {
          this.error("ID do paciente não encontrado na URL.");
          return;
        }
        const { data } = await http.get(
          `/api/prescription/get-all-prescriptions/${patientId}`,
        );
        const prescriptionData = data?.data ?? data;

        if (!Array.isArray(prescriptionData) || prescriptionData.length === 0) {
          this.warning(
            data?.message || "Nenhuma prescrição encontrada para este paciente",
          );
          this.prescriptions = [];
          return;
        }

        this.prescriptions = prescriptionData.map((prescription) => ({
          id: prescription.id,
          created_at: prescription.created_at
            ? formatDate(prescription.created_at)
            : "-",
          prescription_type: prescription.prescription_type || "-",
          total_medications: prescription.total_medications || 0,
          items_count: (prescription.items || []).length,
          status: prescription.status || "-",
          // items já vêm com `conditions` (CIDs) injetados pelo backend.
          items: prescription.items || [],
          // Dados conectados exibidos nos detalhes:
          conditions: prescription.conditions || [], // CIDs da prescrição (união dos itens)
          doctor: prescription.doctor || null, // quem prescreveu
          full_content: prescription.full_content || "", // conteúdo completo (texto)
        }));

        const total = data?.total ?? this.prescriptions.length;
        this.success(
          `${total} prescriç${
            total === 1 ? "ão carregada" : "ões carregadas"
          } com sucesso`,
        );
      } catch (err) {
        console.error("Erro ao carregar prescrições", err);
        const msg =
          err.response?.data?.message ||
          "Erro ao carregar lista de prescrições";
        this.erroDeRequisicao(err, msg);
        this.prescriptions = [];
      } finally {
        this.loading = false;
      }
    },
    viewPrescriptionDetails(prescription) {
      this.selectedPrescription = prescription;
      this.showDetailsDialog = true;
    },
    /**
     * Carrega, uma única vez por sessão da tela, o timbre que o usuário
     * ESCOLHEU no gerenciador — que pode ser o pessoal ou o de uma clínica.
     * A imagem vem em dataURL (o PDF é montado num nó offscreen, então uma
     * blob URL também serviria, mas o dataURL evita depender do ciclo de vida
     * do objeto e simplifica o revoke).
     *
     * Quando o timbre é de uma clínica, o id dela também volta: é em nome
     * dessa clínica que a receita está sendo emitida, e o backend usa isso
     * para preencher CNPJ/endereço do emitente.
     */
    async loadLetterheadForPdf() {
      if (this._letterheadPromise) return this._letterheadPromise;

      this._letterheadPromise = (async () => {
        let blobUrl = null;
        try {
          const letterhead = await resolvePreferredLetterhead();
          if (!letterhead?.id) {
            return { imageDataUrl: null, contentArea: null, clinicId: null };
          }

          blobUrl = await fetchLetterheadImageUrl(letterhead.id);
          const imageDataUrl = await this.blobUrlToDataUrl(blobUrl);
          return {
            imageDataUrl,
            contentArea: letterhead.content_area || null,
            clinicId:
              letterhead.owner_type === "clinic" ? letterhead.owner_id : null,
          };
        } catch (err) {
          // Timbre é opcional: sem ele o PDF sai com o cabeçalho padrão.
          // Falha aqui é rede/servidor, não "não tem timbre" — por isso o
          // cache é descartado, senão um erro passageiro deixaria todos os
          // PDFs da sessão sem timbre.
          console.warn("Não foi possível carregar o timbre para o PDF:", err);
          this._letterheadPromise = null;
          return { imageDataUrl: null, contentArea: null, clinicId: null };
        } finally {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
        }
      })();

      return this._letterheadPromise;
    },

    async blobUrlToDataUrl(blobUrl) {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    async downloadPrescription(prescription) {
      if (!prescription?.id || this.downloadingId !== null) return;

      this.downloadingId = prescription.id;
      try {
        // O timbre resolve primeiro porque é ele que define em nome de qual
        // clínica a receita sai — e o documento precisa disso para montar a
        // identificação do emitente.
        const { imageDataUrl, contentArea, clinicId } =
          await this.loadLetterheadForPdf();

        const documentData = await getPrescriptionDocument(prescription.id, {
          clinicId,
        });

        if (!documentData) {
          this.error("Não foi possível carregar os dados da prescrição.");
          return;
        }

        const pages = await downloadPrescriptionPdf({
          documentData,
          letterheadImageUrl: imageDataUrl,
          contentArea,
          fileName: buildPrescriptionFileName(documentData.prescription),
        });

        this.success(
          `PDF da prescrição #${prescription.id} gerado (${pages} ${
            pages === 1 ? "página" : "páginas"
          }).`,
        );
      } catch (err) {
        console.error("Erro ao gerar PDF da prescrição", err);
        this.erroDeRequisicao(err, 
          err.response?.data?.message ||
            "Não foi possível gerar o PDF da prescrição.",
        );
      } finally {
        this.downloadingId = null;
      }
    },
    getStatusColor(status) {
      const statusColors = {
        ativo: "green",
        active: "green",
        cancelada: "red",
        cancelled: "red",
        pendente: "orange",
        pending: "orange",
        finalizada: "blue",
        completed: "blue",
      };
      return statusColors[status?.toLowerCase()] || "blue-grey";
    },
    getTypeColor(type) {
      const typeColors = {
        medication: "primary",
        exam: "success",
        supplement: "warning",
        mixed: "deep-purple",
      };
      return typeColors[type?.toLowerCase()] || "blue-grey";
    },
    getTypeIcon(type) {
      const icons = {
        medication: "mdi-pill",
        exam: "mdi-test-tube",
        supplement: "mdi-leaf",
        mixed: "mdi-view-list",
      };
      return icons[type?.toLowerCase()] || "mdi-file-document";
    },
    translateType(type) {
      const translations = {
        medication: "Medicamentos",
        exam: "Exames",
        supplement: "Suplementos",
        mixed: "Misto",
      };
      return translations[type?.toLowerCase()] || type;
    },
    getItemsTitle(type) {
      const titles = {
        medication: "Medicamentos Prescritos",
        exam: "Exames Solicitados",
        supplement: "Suplementos Prescritos",
        mixed: "Itens Prescritos",
      };
      return titles[type?.toLowerCase()] || "Itens Prescritos";
    },
    getNoItemsText(type) {
      const texts = {
        medication: "Nenhum medicamento encontrado",
        exam: "Nenhum exame encontrado",
        supplement: "Nenhum suplemento encontrado",
        mixed: "Nenhum item encontrado",
      };
      return texts[type?.toLowerCase()] || "Nenhum item encontrado";
    },
    getTypeTooltip(type) {
      const tooltips = {
        medication: "Prescrição de medicamentos",
        exam: "Solicitação de exames",
        supplement: "Prescrição de suplementos",
        mixed: "Prescrição com itens de múltiplos tipos",
      };
      return tooltips[type?.toLowerCase()] || "Tipo não identificado";
    },
    getStatusTooltip(status) {
      const tooltips = {
        active: "Prescrição ativa e válida",
        ativo: "Prescrição ativa e válida",
        pending: "Aguardando execução",
        pendente: "Aguardando execução",
        cancelled: "Prescrição cancelada",
        cancelada: "Prescrição cancelada",
        completed: "Prescrição finalizada",
        finalizada: "Prescrição finalizada",
      };
      return tooltips[status?.toLowerCase()] || "Status não identificado";
    },
    async confirmDelete(prescription) {
      this.prescriptionToDelete = prescription;
      const ok = await this.$confirm({
        type: "delete",
        title: "Excluir prescrição",
        message: `Tem certeza que deseja excluir a prescrição #${prescription?.id || ""} de ${prescription?.created_at || ""}?`,
        hint: "Esta ação pode ser desfeita posteriormente (exclusão lógica).",
      });
      if (!ok) {
        this.prescriptionToDelete = null;
        return;
      }
      await this.deletePrescription();
    },
    async deletePrescription() {
      if (!this.prescriptionToDelete) return;
      this.isDeleting = true;
      try {
        await http.delete(
          `/api/prescription/delete/${this.prescriptionToDelete.id}`,
        );
        this.success("Prescrição excluída com sucesso!");
        this.prescriptionToDelete = null;
        await this.loadPrescriptions();
      } catch (err) {
        console.error("Erro ao excluir prescrição", err);
        const msg =
          err.response?.data?.message ||
          "Erro ao excluir prescrição. Tente novamente.";
        this.erroDeRequisicao(err, msg);
      } finally {
        this.isDeleting = false;
      }
    },
    translateStatus(status) {
      const translations = {
        active: "Ativo",
        ativo: "Ativo",
        cancelada: "Cancelada",
        cancelled: "Cancelada",
        pendente: "Pendente",
        pending: "Pendente",
        finalizada: "Finalizada",
        completed: "Finalizada",
      };
      return translations[status?.toLowerCase()] || status;
    },
    openPrescriptionDialog() {
      this.showPrescriptionDialog = true;
    },
    openLetterheadDialog() {
      this.showLetterheadDialog = true;
    },
    handlePrescriptionSaved() {
      this.success("Prescrição criada com sucesso!");
      this.showPrescriptionDialog = false;
      this.loadPrescriptions();
    },
    handleLetterheadUpdated() {
      this.success("Timbre atualizado com sucesso!");
    },
    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (this.showSearch) {
        this.$nextTick(() => this.$refs.searchField?.focus());
      } else {
        this.search = "";
      }
    },
    closeSearch() {
      this.showSearch = false;
      this.search = "";
    },
    /**
     * Texto único e normalizado usado pelo filtro. Cobre o que aparece na
     * tabela (data, tipo, status) e também o que só está nos detalhes —
     * nome dos itens, código TUSS e CIDs — porque é por aí que o médico
     * costuma procurar ("losartana", "E11", "hemograma").
     */
    buildSearchHaystack(prescription) {
      if (prescription.__haystack) return prescription.__haystack;

      const parts = [
        `#${prescription.id}`,
        prescription.id,
        prescription.created_at,
        prescription.prescription_type,
        this.translateType(prescription.prescription_type),
        prescription.status,
        this.translateStatus(prescription.status),
        prescription.doctor?.name,
      ];

      for (const item of prescription.items || []) {
        parts.push(
          item.medication_name,
          item.tuss_code,
          item.presentation,
          item.quantity,
          item.dosage,
          item.frequency,
          item.duration,
          item.administration_route,
          item.instructions,
          item.notes,
          this.translateType(item.item_type),
        );
        for (const cid of item.conditions || []) {
          parts.push(cid.code, cid.description);
        }
      }

      for (const cid of prescription.conditions || []) {
        parts.push(cid.code, cid.description);
      }

      const haystack = parts
        .filter((v) => v != null && v !== "")
        .join(" ")
        .toLowerCase();

      // Cache não-reativo: a lista só muda via loadPrescriptions, que recria
      // os objetos, então não há risco de o haystack ficar defasado.
      Object.defineProperty(prescription, "__haystack", {
        value: haystack,
        enumerable: false,
        configurable: true,
      });
      return haystack;
    },
    sortprescriptions(order) {
      this.prescriptions = this.prescriptions.sort((a, b) => {
        const dateA = new Date(a.created_at.split("/").reverse().join("-"));
        const dateB = new Date(b.created_at.split("/").reverse().join("-"));
        return order === "asc" ? dateA - dateB : dateB - dateA;
      });
      this.menuOpen = false;
      this.info(
        `Ordenado em ordem ${order === "asc" ? "crescente" : "decrescente"}`,
      );
    },
  },
};
</script>

<style scoped>
.info-item {
  padding: 8px 0;
  font-size: 14px;
}

/* Contador de resultados dentro do campo de busca */
.search-count {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: var(--ds-text-500, #78909c);
  white-space: nowrap;
  line-height: 20px;
}

/* Detalhes: descrição do CID (limita largura no chip) */
.cid-desc {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}

/* Detalhes: bloco de conteúdo completo (texto integral) */
.full-content-box {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ds-text-700, #37474f);
  background: var(--ds-blue-50, #f1f5f9);
  border: 1px solid var(--ds-blue-100, #e2e8f0);
  border-radius: 10px;
  padding: 14px 16px;
  margin: 0;
}

.info-item strong {
  color: var(--ds-primary);
  margin-right: 8px;
}

/* Melhorar aparência da tabela de detalhes */
.v-dialog-card-border {
  border-radius: 8px;
}

/* Estilo para o texto truncado */
.text-truncate {
  cursor: help;
}

/* ═══ Page title ═══ */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}

/* ═══ Tabela padronizada (estilo BodyCompositionDialog / Goals) ═══ */
.v-data-table >>> thead th,
.v-simple-table >>> thead th {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 12px !important;
  font-size: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.4px !important;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
  height: auto !important;
}

.v-data-table >>> tbody tr,
.v-simple-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.v-data-table >>> tbody tr:hover,
.v-simple-table >>> tbody tr:hover {
  background-color: var(--ds-blue-50) !important;
}

.v-data-table >>> tbody td,
.v-simple-table >>> tbody td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px !important;
  color: var(--ds-text-600) !important;
}

/* ═══ Célula de ações ═══ */
.actions-cell-inner {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
